import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { MapPin, Phone, Instagram, Download, Star, ShoppingBag, Truck } from "lucide-react";
import { Reveal } from "./Reveal";
import { TikTokIcon } from "./icons";
import { LINKS, INFO, IMAGES } from "../../data";

export const Location = () => {
  const comingSoon = (what) =>
    toast(`${what} — coming soon!`, {
      description: "We're cooking it up. Follow us on Instagram for updates.",
    });

  return (
    <section id="location" className="relative bg-black px-5 sm:px-8 lg:px-12 py-24 sm:py-32 border-t border-neon-green/15">
      <div className="mx-auto max-w-[1600px] grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20 items-center">
        {/* Left: info + CTAs */}
        <div>
          <Reveal>
            <span className="font-graffiti text-neon-yellow text-4xl sm:text-5xl -rotate-2 inline-block">come thru</span>
            <h2 className="font-heading text-6xl sm:text-7xl lg:text-8xl text-neon-green uppercase tracking-tighter leading-none neon-glow-green mt-1">
              Find Us
            </h2>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="mt-10 flex flex-col gap-5">
              <a href={LINKS.maps} target="_blank" rel="noopener noreferrer" data-testid="address-link" className="group flex items-start gap-4 text-white hover:text-neon-green transition-colors">
                <MapPin className="text-neon-green shrink-0 mt-1" size={26} />
                <span className="font-heading text-3xl sm:text-4xl uppercase tracking-wide leading-none">{INFO.address}</span>
              </a>
              <a href={LINKS.phone} data-testid="phone-link" className="group flex items-center gap-4 text-white hover:text-neon-green transition-colors">
                <Phone className="text-neon-green shrink-0" size={24} />
                <span className="font-body text-lg">{INFO.phone}</span>
              </a>
              <a href={LINKS.instagram} target="_blank" rel="noopener noreferrer" data-testid="instagram-link" className="group flex items-center gap-4 text-white hover:text-neon-green transition-colors">
                <Instagram className="text-neon-green shrink-0" size={24} />
                <span className="font-body text-lg">{INFO.instagram}</span>
              </a>
              <a href={LINKS.tiktok} target="_blank" rel="noopener noreferrer" data-testid="tiktok-link" className="group flex items-center gap-4 text-white hover:text-neon-green transition-colors">
                <TikTokIcon className="text-neon-green shrink-0" size={22} />
                <span className="font-body text-lg">{INFO.tiktok} · TikTok</span>
              </a>
            </div>
          </Reveal>

          {/* CTA buttons */}
          <Reveal delay={0.2}>
            <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Link
                to="/order"
                data-testid="cta-order-btn"
                className="sm:col-span-2 group inline-flex items-center justify-center gap-3 font-heading text-2xl tracking-wide bg-neon-green text-black px-8 py-5 hover:bg-neon-yellow transition-colors duration-200 active:scale-[0.98]"
              >
                <ShoppingBag size={24} /> Order Online / Pickup
              </Link>
              <a
                href={LINKS.doordash}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="cta-delivery-btn"
                className="sm:col-span-2 group inline-flex items-center justify-center gap-3 font-heading text-xl tracking-wide border border-neon-yellow text-neon-yellow px-6 py-4 hover:bg-neon-yellow hover:text-black transition-colors duration-200 active:scale-[0.98]"
              >
                <Truck size={22} /> Get Delivery on DoorDash
              </a>
              <button
                data-testid="cta-download-app-btn"
                onClick={() => comingSoon("Our App")}
                className="inline-flex items-center justify-center gap-3 font-heading text-xl tracking-wide border border-neon-green text-neon-green px-6 py-4 hover:bg-neon-green hover:text-black transition-colors duration-200 active:scale-[0.98]"
              >
                <Download size={20} /> Download App
              </button>
              <a
                href={LINKS.googleReview}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="cta-google-review-btn"
                className="inline-flex items-center justify-center gap-3 font-heading text-xl tracking-wide border border-neon-yellow text-neon-yellow px-6 py-4 hover:bg-neon-yellow hover:text-black transition-colors duration-200 active:scale-[0.98]"
              >
                <Star size={20} /> Google Review
              </a>
              <a
                href={LINKS.yelp}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="cta-yelp-btn"
                className="inline-flex items-center justify-center gap-3 font-heading text-xl tracking-wide border border-neon-green text-neon-green px-6 py-4 hover:bg-neon-green hover:text-black transition-colors duration-200 active:scale-[0.98]"
              >
                <Star size={20} /> Review on Yelp
              </a>
            </div>
          </Reveal>
        </div>

        {/* Right: Yelp review collage */}
        <Reveal delay={0.15}>
          <a
            href={LINKS.yelp}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="yelp-image-link"
            className="block"
          >
          <motion.div
            initial={{ rotate: -1.5 }}
            whileHover={{ rotate: 0, scale: 1.01 }}
            transition={{ duration: 0.4 }}
            className="relative overflow-hidden border border-neon-yellow/30 max-w-md mx-auto"
          >
            <img src={IMAGES.yelp} alt="67 Wings 5-star reviews on Yelp" className="w-full object-cover" />
            <div className="absolute inset-0 ring-1 ring-inset ring-neon-green/10 pointer-events-none" />
          </motion.div>
          </a>
        </Reveal>
      </div>
    </section>
  );
};

export default Location;
